import { LC_WORKSPACE_CARD_LIMIT } from '../constants/lcWorkspaces';
import { useEffect, useState } from 'react';
import FirstCardModal from './FirstCardModal';
import MultiSelect, { type MultiSelectOption } from './MultiSelect';
import { lcManagedWorkspacesService as service, type ManagedWorkspace, type ManagedCard } from '../services/lcManagedWorkspaces.service';

const blankWorkspace = (): ManagedWorkspace => ({ id: 0, name: '', revision: 0, config: { recipients: [], cards: [] } });

export default function LCManagedWorkspacesModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => Promise<void> }) {
  const [workspaces, setWorkspaces] = useState<ManagedWorkspace[]>([]);
  const [options, setOptions] = useState<MultiSelectOption[]>([]);
  const [draft, setDraft] = useState<ManagedWorkspace>(blankWorkspace);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [editingCard, setEditingCard] = useState<ManagedCard | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);

  useEffect(() => {
    let active = true;
    service.list().then(data => {
      if (active) { setWorkspaces(data.workspaces); setOptions(data.recipients); }
    }).catch(() => { if (active) setError('Could not load required workspaces. Close and try again.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const changeCard = (id: string, patch: Partial<ManagedCard>) => setDraft(prev => ({ ...prev,
    config: { ...prev.config, cards: prev.config.cards.map(c => c.id === id ? { ...c, ...patch } : c) },
  }));
  const save = async (remove = false) => {
    setBusy(true); setError('');
    try {
      if (remove) await service.remove(draft); else await service.save(draft);
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: unknown } } }).response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Could not save. Check the card filters and recipients, then try again.');
      setBusy(false);
      return;
    }
    setSaved(true);
    try { await onSaved(); onClose(); }
    catch { setError('Saved, but the dashboard could not refresh. Reload the page before making more changes.'); }
    finally { setBusy(false); }
  };

  if (editingCard) return <FirstCardModal
    isOpen dashboardType="lc" hideStatButtonSettings
    isEditMode={draft.config.cards.some(c => c.id === editingCard.id)}
    initialCardName={editingCard.config.title}
    initialConditions={editingCard.config.filterConditions}
    initialView={editingCard.config.filterView || 'Orders'}
    initialBucket={editingCard.config.bucket}
    initialIncludeClosed={editingCard.config.includeClosed}
    onClose={() => setEditingCard(null)}
    onSubmit={(title, filterConditions, filterView, bucket, _label, _count, _show, _shared, _table, includeClosed) => {
      const card = { ...editingCard, config: { ...editingCard.config, title, filterConditions, filterView, bucket, includeClosed } };
      setDraft(prev => ({ ...prev, config: { ...prev.config, cards: prev.config.cards.some(c => c.id === card.id)
        ? prev.config.cards.map(c => c.id === card.id ? card : c) : [...prev.config.cards, card] } }));
      setEditingCard(null);
    }}
  />;

  return <div className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4">
    <section role="dialog" aria-modal="true" aria-labelledby="lc-required-heading" className="bg-panel text-heading rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto p-6 space-y-4">
      <div className="flex justify-between items-center">
        <h2 id="lc-required-heading" className="text-lg font-semibold">Manage Required LC Workspaces</h2>
        <button onClick={onClose} disabled={busy} aria-label="Close required workspaces">Close</button>
      </div>
      <p className="text-sm">Assigned LCs receive this workspace automatically. Each card applies its saved filters together with the filters at the top of the dashboard. Group assignments follow current membership when the dashboard loads.</p>
      {error && <p role="alert" className="text-red-600">{error}</p>}
      {loading ? <p>Loading…</p> : <fieldset disabled={busy || saved || (!!error && options.length === 0)} className="space-y-4">
        <label className="block">Workspace
          <select aria-label="Choose required workspace" className="block w-full bg-panel border rounded p-2" value={draft.id}
            onChange={e => { setDraft(workspaces.find(w => w.id === Number(e.target.value)) || blankWorkspace()); setConfirmRemove(false); setError(''); }}>
            <option value={0}>Create required workspace</option>
            {workspaces.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>
        </label>
        <label className="block">Workspace name
          <input aria-label="Workspace name" className="block w-full bg-panel border rounded p-2" value={draft.name} maxLength={255}
            onChange={e => setDraft({ ...draft, name: e.target.value })} />
        </label>
        <div><p className="font-medium mb-1">Workspace recipients</p>
          <MultiSelect options={options} value={draft.config.recipients} enableSearch placeholder="Select users or LC groups"
            onChange={recipients => setDraft({ ...draft, config: { ...draft.config, recipients } })} />
        </div>
        <p className="text-sm">Cards inherit workspace recipients. Select recipients on a card to override that list. Recipients cannot rename, remove, or change required cards.</p>
        {draft.config.cards.map(card => <div key={card.id} className="border rounded p-3 space-y-2">
          <div className="flex justify-between gap-3"><strong>{card.config.title}</strong>
            <div className="flex gap-3"><button onClick={() => setEditingCard(card)}>Edit card / filters</button>
              <button onClick={() => setDraft({ ...draft, config: { ...draft.config, cards: draft.config.cards.filter(c => c.id !== card.id) } })}>Remove card</button></div>
          </div>
          <MultiSelect options={options} value={card.recipients} enableSearch placeholder="Inherit workspace recipients"
            onChange={recipients => changeCard(card.id, { recipients })} />
        </div>)}
        <button disabled={draft.config.cards.length >= LC_WORKSPACE_CARD_LIMIT} className="border rounded px-3 py-2" onClick={() => setEditingCard({ id: crypto.randomUUID(), recipients: [], config: { title: '' } })}>Add required card ({draft.config.cards.length}/{LC_WORKSPACE_CARD_LIMIT})</button>
        <div className="flex justify-between items-center border-t pt-4">
          {draft.id > 0 ? <button className="text-red-600" onClick={() => setConfirmRemove(true)}>Remove workspace…</button> : <span />}
          <button className="bg-accent text-accent-fg rounded px-4 py-2" disabled={!draft.name.trim() || !draft.config.cards.length || draft.config.cards.some(c => !c.recipients.length && !draft.config.recipients.length)} onClick={() => void save()}>{busy ? 'Saving…' : 'Save assignments'}</button>
        </div>
        {confirmRemove && <div className="border border-red-400 rounded p-3">
          <p>Remove this required workspace from all assigned LCs?</p>
          <button className="text-red-600 mr-4" onClick={() => void save(true)}>Remove from all recipients</button>
          <button onClick={() => setConfirmRemove(false)}>Cancel</button>
        </div>}
      </fieldset>}
    </section>
  </div>;
}
